/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import { FaTimes, FaMapMarkerAlt } from 'react-icons/fa'
import { shipmentsService } from '../services/shipmentsService'
import LocationModal from './LocationModal.jsx'
import { getShipmentRequester, getShipmentRequesterName } from '../utils/shipmentHelpers'

const numberToWords = (num) => {
  if (num === 0) return 'Zero Only'
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const numToWords = (n) => {
    if (n < 20) return a[n]
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '')
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '')
    if (n < 1000000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '')
    if (n < 1000000000) return numToWords(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + numToWords(n % 1000000) : '')
    return num.toString()
  }
  return numToWords(num) + ' Only'
}

const getPlatformDisplay = (shipmentData) => {
  const platformValue = shipmentData?.platform || shipmentData?.source || shipmentData?.submittedFrom ||
    shipmentData?.bookingPlatform || shipmentData?.bookingSource || shipmentData?.submissionPlatform
  if (!platformValue) return { text: 'N/A', isMobile: false }
  const p = platformValue.toString().toLowerCase().trim()
  const isMobile = ['mobile', 'app', 'cargo360-client-app', 'android', 'ios'].includes(p)
  const isWeb = ['web', 'portal', 'cargo360-client-portal', 'website'].includes(p)
  if (isMobile) return { text: 'Mobile', isMobile: true }
  if (isWeb) return { text: 'Web', isMobile: false }
  return { text: platformValue, isMobile: false }
}

const statusBadge = (status) => {
  const map = {
    delivered:  'bg-[#eafaf1] text-[#27ae60]',
    accepted:   'bg-[#eaf4fb] text-[#3498db]',
    in_transit: 'bg-[#fef5e7] text-[#e67e22]',
    picked_up:  'bg-[#fef5e7] text-[#e67e22]',
    cancelled:  'bg-[#fdedec] text-[#e74c3c]',
  }
  return map[status] || 'bg-[#f8f9fa] text-[#7f8c8d]'
}

const Field = ({ label, value, children }) => (
  <div>
    <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">{label}</p>
    {children || <p className="text-sm text-[#2c3e50]">{value || '—'}</p>}
  </div>
)

const Section = ({ title, children }) => (
  <div className="bg-white border border-[#e9ecef] rounded-lg p-5">
    <h3 className="text-sm font-semibold text-[#2c3e50] pb-3 mb-4 border-b border-[#e9ecef]">{title}</h3>
    {children}
  </div>
)

const OrderModal = ({ order, onClose }) => {
  const [showLocationModal, setShowLocationModal] = useState(false)

  if (!order) return null

  const shipmentData = order.shipmentData || {}
  const isTrackable = shipmentData.status === 'in_transit' || shipmentData.status === 'picked_up'
  const platformDisplay = getPlatformDisplay(shipmentData)
  const requester = getShipmentRequester(shipmentData)

  const handleAcceptDiscountRequest = async (discountRequestId, action) => {
    const result = await shipmentsService.decideDiscountRequest(discountRequestId, action)
    if (result.success) { window.alert(result.message || 'Decision recorded'); onClose() }
    else window.alert(result.error || result.message || 'Failed to decide discount request')
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
      <div className="bg-[#f8f9fa] rounded-lg w-full max-w-[900px] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[#e9ecef] px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-base font-semibold text-[#2c3e50]">Shipment Details</h2>
          <div className="flex items-center gap-3">
            {isTrackable && (
              <button onClick={() => setShowLocationModal(true)}
                className="flex items-center gap-1.5 bg-[#3498db] hover:bg-[#2980b9] text-white text-sm px-3 py-1.5 rounded-lg transition-colors">
                <FaMapMarkerAlt size={12} /> View Location
              </button>
            )}
            <button onClick={onClose} className="text-[#7f8c8d] hover:text-[#2c3e50] transition-colors">
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Summary bar */}
          <div className="bg-white border border-[#e9ecef] rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Shipment ID" value={order.orderId} />
            <Field label="Customer" value={order.customer || getShipmentRequesterName(shipmentData)} />
            <Field label="Status">
              <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${statusBadge(shipmentData.status)}`}>
                {shipmentData.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Pending'}
              </span>
            </Field>
            <Field label="Platform">
              <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                platformDisplay.isMobile ? 'bg-[#f0e6ff] text-[#8e44ad]' : 'bg-[#eaf4fb] text-[#3498db]'
              }`}>
                {platformDisplay.text}
              </span>
            </Field>
          </div>

          {/* Location */}
          <Section title="Location Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Pickup Location" value={shipmentData.pickupLocation} />
              <Field label="Drop Location" value={shipmentData.dropLocation} />
            </div>
          </Section>

          {/* Customer */}
          <Section title="Customer Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" value={requester.name} />
              <Field label="Phone" value={requester.phone} />
              <Field label="Company" value={requester.company} />
              <Field label="Email" value={requester.email} />
            </div>
          </Section>

          {/* Cargo */}
          <Section title="Cargo Information">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <Field label="Cargo Type" value={shipmentData.cargoType} />
              <Field label="Vehicle Type" value={shipmentData.vehicleType} />
              <Field label="Cargo Weight" value={shipmentData.cargoWeight ? `${shipmentData.cargoWeight} kg` : null} />
              <Field label="No. of Containers / Vehicles" value={shipmentData.numberOfVehicles} />
              <Field label="Estimated Budget" value={shipmentData.budget?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
            </div>

            {shipmentData.DiscountRequest && (
              <div className="bg-[#fef9e7] border border-[#f39c12]/30 rounded-lg p-4 mb-4">
                <p className="text-xs font-semibold text-[#7f8c8d] uppercase tracking-wide mb-3">Discount Request</p>
                <div className="space-y-1.5 text-sm text-[#2c3e50]">
                  <p>Client requesting a discount of: <strong>{shipmentData.DiscountRequest.requestAmount}</strong></p>
                  <p>Status: <span className={`font-semibold ${shipmentData.DiscountRequest.status === 'pending' ? 'text-[#e67e22]' : 'text-[#7f8c8d]'}`}>{shipmentData.DiscountRequest.status}</span></p>
                  {shipmentData.budget && (
                    <>
                      <p className="font-semibold mt-2">
                        Total Budget: PKR {shipmentData.totalAmount
                          ? (parseFloat(shipmentData.budget) - parseFloat(shipmentData.totalAmount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : parseFloat(shipmentData.budget).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        }
                      </p>
                      <p className="text-xs text-[#7f8c8d]">
                        {numberToWords(shipmentData.totalAmount
                          ? parseFloat(shipmentData.budget) - parseFloat(shipmentData.totalAmount)
                          : parseFloat(shipmentData.budget)
                        )}
                      </p>
                    </>
                  )}
                  {shipmentData.DiscountRequest.status === 'pending' && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => handleAcceptDiscountRequest(shipmentData.DiscountRequest.id, 'accept')}
                        className="bg-[#27ae60] hover:bg-[#219a52] text-white text-sm px-4 py-1.5 rounded-lg transition-colors">
                        Accept
                      </button>
                      <button onClick={() => handleAcceptDiscountRequest(shipmentData.DiscountRequest.id, 'reject')}
                        className="bg-[#e74c3c] hover:bg-[#c0392b] text-white text-sm px-4 py-1.5 rounded-lg transition-colors">
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {shipmentData.description && (
              <Field label="Description" value={shipmentData.description} />
            )}
          </Section>

          {/* Assignment */}
          {(shipmentData.Trucker || shipmentData.Driver) && (
            <Section title="Assignment Details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shipmentData.Trucker && (
                  <div>
                    <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-2">Assigned Trucker</p>
                    <p className="text-sm font-semibold text-[#2c3e50]">{shipmentData.Trucker.name}</p>
                    <p className="text-xs text-[#7f8c8d]">ID: {shipmentData.Trucker.id} · {shipmentData.Trucker.email}</p>
                    {shipmentData.Trucker.phone && <p className="text-xs text-[#7f8c8d]">Phone: {shipmentData.Trucker.phone}</p>}
                  </div>
                )}
                {shipmentData.Driver && (
                  <div>
                    <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-2">Assigned Driver</p>
                    <p className="text-sm font-semibold text-[#2c3e50]">{shipmentData.Driver.name}</p>
                    <p className="text-xs text-[#7f8c8d]">ID: {shipmentData.Driver.id} · {shipmentData.Driver.email}</p>
                    {shipmentData.Driver.phone && <p className="text-xs text-[#7f8c8d]">Phone: {shipmentData.Driver.phone}</p>}
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Timeline */}
          {(shipmentData.createdAt || shipmentData.updatedAt || shipmentData.deliveryDate || order.deliveryDate) && (
            <Section title="Timeline">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {shipmentData.createdAt && (
                  <Field label="Created At" value={new Date(shipmentData.createdAt).toLocaleString()} />
                )}
                {(shipmentData.deliveryDate || order.deliveryDate) && (
                  <Field label="Delivery Date" value={shipmentData.deliveryDate || order.deliveryDate} />
                )}
                {shipmentData.updatedAt && (
                  <Field label="Last Updated" value={new Date(shipmentData.updatedAt).toLocaleString()} />
                )}
              </div>
            </Section>
          )}
        </div>
      </div>

      {showLocationModal && (
        <LocationModal
          shipment={{
            orderId: order.orderId,
            shipmentId: order.orderId.replace('C360-PK-', ''),
            customer: order.customer || getShipmentRequesterName(shipmentData),
            status: shipmentData.status,
            shipmentData: shipmentData,
          }}
          onClose={() => setShowLocationModal(false)}
        />
      )}
    </div>
  )
}

export default OrderModal
